/**
 * Microsoft Graph — calendar client.
 *
 * Wraps `/me/calendarView` for the unified Appointments landing.
 *
 * Auth model:
 *   - Inside Teams desktop, MSAL or Teams SSO returns a delegated token.
 *     Required scope: `Calendars.ReadWrite` (also covers `Calendars.Read`).
 *   - Outside Teams in dev, MSAL same scope.
 *
 * Today this module is FEATURE-FLAGGED off (`VITE_FEATURE_APPOINTMENTS_LIVE`).
 * When the flag is `'true'` and a bearer token is available the client makes
 * real Graph calls; otherwise it returns an empty list so the UI renders the
 * zero state without an error toast. A separate `VITE_FEATURE_APPOINTMENTS_MOCK`
 * flag injects deterministic fixtures so the demo path stays sleek.
 *
 * NOTE — token acquisition happens via the existing MSAL service. We do NOT
 * touch the Function (`PCBAccountOpeningApi`); this is an SSO-on-the-client
 * call. The Function gets a future `/v3/appointments/*` route only when we
 * need server-side aggregation across all officers (Manage tab).
 */

import type { Appointment } from '../types';
import { mockAppointmentsForToday, mockAppointmentsForUpcoming } from './mockAppointments';

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

function liveEnabled(): boolean {
  // Browser-only — guarded for SSR safety even though this app is CSR.
  const env = (import.meta as unknown as { env: Record<string, string | undefined> }).env;
  return env['VITE_FEATURE_APPOINTMENTS_LIVE'] === 'true';
}

function mockEnabled(): boolean {
  const env = (import.meta as unknown as { env: Record<string, string | undefined> }).env;
  return env['VITE_FEATURE_APPOINTMENTS_MOCK'] === 'true';
}

interface GraphEvent {
  id: string;
  subject?: string;
  bodyPreview?: string;
  start?: { dateTime: string; timeZone: string };
  end?: { dateTime: string; timeZone: string };
  isOnlineMeeting?: boolean;
  onlineMeetingUrl?: string;
  onlineMeeting?: { joinUrl?: string };
  location?: { displayName?: string };
  attendees?: ReadonlyArray<{
    type?: string;
    emailAddress?: { name?: string; address?: string };
  }>;
}

interface GraphCalendarViewResponse {
  value: ReadonlyArray<GraphEvent>;
  '@odata.nextLink'?: string;
}

function isoStartOfDay(d: Date): string {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString();
}

function isoEndOfDay(d: Date): string {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.toISOString();
}

function deriveCustomerName(ev: GraphEvent): string {
  const subject = ev.subject ?? '';
  // Graph often returns "Open Account · Connor Sample" — keep customer half.
  const parts = subject.split(/\s+[·\-—]\s+/);
  if (parts.length >= 2) return (parts[1] ?? '').trim() || subject;

  // Fallback to first non-organizer attendee.
  const attendee = ev.attendees?.find((a) => a.type !== 'organizer');
  return attendee?.emailAddress?.name ?? subject ?? 'Customer';
}

function deriveSubject(ev: GraphEvent): string {
  const subject = ev.subject ?? '';
  const parts = subject.split(/\s+[·\-—]\s+/);
  if (parts.length >= 2) return (parts[0] ?? '').trim() || subject;
  return subject || 'Appointment';
}

function deriveChannel(ev: GraphEvent): Appointment['channel'] {
  if (ev.isOnlineMeeting || ev.onlineMeeting?.joinUrl) return 'teams';
  if ((ev.location?.displayName ?? '').toLowerCase().includes('phone')) return 'phone';
  return 'inBranch';
}

function eventToAppointment(ev: GraphEvent, branchCode?: string): Appointment {
  const start = ev.start?.dateTime ?? new Date().toISOString();
  const end = ev.end?.dateTime ?? new Date(Date.now() + 30 * 60_000).toISOString();
  const out: Appointment = {
    id: ev.id,
    customerName: deriveCustomerName(ev),
    subject: deriveSubject(ev),
    startUtc: start,
    endUtc: end,
    timeZone: ev.start?.timeZone ?? 'UTC',
    channel: deriveChannel(ev),
    source: 'calendar',
  };
  if (branchCode) out.branchCode = branchCode;
  if (ev.location?.displayName) out.branchName = ev.location.displayName;
  if (ev.bodyPreview) out.notes = ev.bodyPreview;
  const join = ev.onlineMeeting?.joinUrl ?? ev.onlineMeetingUrl;
  if (join) out.joinWebUrl = join;
  return out;
}

async function callGraph<T>(path: string, token: string): Promise<T> {
  const res = await fetch(`${GRAPH_BASE}${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      Prefer: 'outlook.timezone="America/Chicago"',
    },
  });
  if (!res.ok) {
    throw new Error(`Graph ${path} failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export interface CalendarFetchOptions {
  /** Bearer token from MSAL / Teams SSO. Required for live calls. */
  token?: string | null;
  /** FSR's branch (used to label cards). */
  branchCode?: string;
}

/**
 * Fetch today's calendar events for the signed-in user, mapped to
 * unified Appointment cards. Returns [] when the feature flag is off.
 */
export async function fetchTodayCalendar(opts: CalendarFetchOptions): Promise<ReadonlyArray<Appointment>> {
  if (!liveEnabled()) {
    return mockEnabled() ? mockAppointmentsForToday(opts.branchCode) : [];
  }
  if (!opts.token) return [];

  const now = new Date();
  const startIso = isoStartOfDay(now);
  const endIso = isoEndOfDay(now);
  const path =
    `/me/calendarView?startDateTime=${encodeURIComponent(startIso)}` +
    `&endDateTime=${encodeURIComponent(endIso)}` +
    `&$orderby=start/dateTime&$top=50` +
    `&$select=id,subject,bodyPreview,start,end,isOnlineMeeting,onlineMeetingUrl,onlineMeeting,location,attendees`;

  try {
    const data = await callGraph<GraphCalendarViewResponse>(path, opts.token);
    return data.value.map((e) => eventToAppointment(e, opts.branchCode));
  } catch (err) {
    // Console-only; UI shows zero state. Don't block render.
    // eslint-disable-next-line no-console
    console.warn('[graphCalendarClient] fetchTodayCalendar failed:', err);
    return [];
  }
}

/**
 * Fetch the next 7 days (excluding today). Sleeps the same fallbacks.
 */
export async function fetchUpcomingCalendar(opts: CalendarFetchOptions): Promise<ReadonlyArray<Appointment>> {
  if (!liveEnabled()) {
    return mockEnabled() ? mockAppointmentsForUpcoming(opts.branchCode) : [];
  }
  if (!opts.token) return [];

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  const seven = new Date(tomorrow);
  seven.setDate(seven.getDate() + 7);

  const path =
    `/me/calendarView?startDateTime=${encodeURIComponent(tomorrow.toISOString())}` +
    `&endDateTime=${encodeURIComponent(seven.toISOString())}` +
    `&$orderby=start/dateTime&$top=100` +
    `&$select=id,subject,bodyPreview,start,end,isOnlineMeeting,onlineMeetingUrl,onlineMeeting,location,attendees`;

  try {
    const data = await callGraph<GraphCalendarViewResponse>(path, opts.token);
    return data.value.map((e) => eventToAppointment(e, opts.branchCode));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[graphCalendarClient] fetchUpcomingCalendar failed:', err);
    return [];
  }
}

/**
 * Create a calendar event (used for "in-branch" + "phone" channels).
 *
 * Returns the new event's id on success. Errors propagate.
 */
export async function createCalendarEvent(
  token: string,
  args: {
    subject: string;
    bodyHtml: string;
    startIsoLocal: string; // local 'YYYY-MM-DDTHH:mm:ss'
    endIsoLocal: string;
    timeZone: string;
    locationDisplay?: string;
    attendees?: ReadonlyArray<{ name: string; email: string }>;
  },
): Promise<string> {
  const body = {
    subject: args.subject,
    body: { contentType: 'HTML', content: args.bodyHtml },
    start: { dateTime: args.startIsoLocal, timeZone: args.timeZone },
    end: { dateTime: args.endIsoLocal, timeZone: args.timeZone },
    location: args.locationDisplay ? { displayName: args.locationDisplay } : undefined,
    attendees: (args.attendees ?? []).map((a) => ({
      type: 'required',
      emailAddress: { name: a.name, address: a.email },
    })),
  };

  const res = await fetch(`${GRAPH_BASE}/me/events`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`Graph POST /me/events failed (${res.status})`);
  }
  const json = (await res.json()) as { id: string };
  return json.id;
}
